import { useState, FormEvent } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const workTypes = formData.getAll('workType');
    formData.delete('workType');
    if (workTypes.length > 0) {
      formData.append('workType', workTypes.join(', '));
    }

    const inquiryContent = formData.get('inquiryContent') as string;
    if (inquiryContent && inquiryContent.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4mod9ku6ftm2u5dbhug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        form.reset();
        onClose();
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccess(false);
  };

  if (!isOpen && !showSuccess) return null;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="flex items-end justify-center h-full">
            <div className="bg-white rounded-t-xl w-full max-w-md h-5/6 overflow-hidden">
              <div className="bg-primary text-white p-4 flex items-center justify-between">
                <h3 className="font-bold text-lg text-green-100">お問い合わせフォーム</h3>
                <button 
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-green-100"></i>
                </button>
              </div>
              <div className="p-4 overflow-y-auto h-full pb-20">
                <form id="contactForm" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input 
                      name="customerName"
                      required 
                      className="w-full px-3 py-2 border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">会社名・団体名</label>
                    <input 
                      name="companyName"
                      className="w-full px-3 py-2 border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">
                      お電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="tel"
                      name="phoneNumber"
                      required 
                      className="w-full px-3 py-2 border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="email"
                      name="emailAddress"
                      required 
                      className="w-full px-3 py-2 border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">
                      お問い合わせ種別 <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input type="radio" name="inquiryType" value="見積もり依頼" required className="mr-2" />
                        <span className="text-sm text-green-700">見積もり依頼</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input type="radio" name="inquiryType" value="工事相談" className="mr-2" />
                        <span className="text-sm text-green-700">工事相談</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input type="radio" name="inquiryType" value="採用について" className="mr-2" />
                        <span className="text-sm text-green-700">採用について</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input type="radio" name="inquiryType" value="その他" className="mr-2" />
                        <span className="text-sm text-green-700">その他</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">工事種別</label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input type="checkbox" name="workType" value="住宅・店舗配線工事" className="mr-2" />
                        <span className="text-sm text-green-700">住宅・店舗配線工事</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input type="checkbox" name="workType" value="工場・ビル電気設備工事" className="mr-2" />
                        <span className="text-sm text-green-700">工場・ビル電気設備工事</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input type="checkbox" name="workType" value="保守メンテナンス" className="mr-2" />
                        <span className="text-sm text-green-700">保守メンテナンス</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input type="checkbox" name="workType" value="高圧・特殊電気工事" className="mr-2" />
                        <span className="text-sm text-green-700">高圧・特殊電気工事</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea 
                      name="inquiryContent"
                      required 
                      rows={5}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm" 
                      placeholder="お問い合わせ内容をできるだけ詳しくご記入ください"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">ご希望の連絡方法</label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input type="radio" name="contactMethod" value="電話" className="mr-2" />
                        <span className="text-sm text-green-700">お電話</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input type="radio" name="contactMethod" value="メール" className="mr-2" />
                        <span className="text-sm text-green-700">メール</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input type="radio" name="contactMethod" value="どちらでも" defaultChecked className="mr-2" />
                        <span className="text-sm text-green-700">どちらでも可</span>
                      </label>
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-800 leading-relaxed">
                      <i className="ri-information-line mr-1"></i>
                      いただいたお問い合わせには、営業時間内(平日 8:00〜17:00)にご連絡いたします。緊急工事の場合は時間外でも対応可能です。
                    </p>
                  </div>
                </form>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-white border-t p-4">
                <button 
                  form="contactForm"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-white py-3 rounded-lg font-medium cursor-pointer whitespace-nowrap hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <i className="ri-send-plane-line mr-2 text-green-100"></i>
                  <span className="text-green-100">{isSubmitting ? '送信中...' : '送信する'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="flex items-center justify-center h-full p-4">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <i className="ri-check-line text-2xl text-green-600"></i>
              </div>
              <h3 className="font-bold text-green-900 mb-2">お問い合わせを受付いたしました</h3>
              <p className="text-sm text-green-600 mb-4">
                営業時間内にご連絡いたします。お急ぎの場合はお電話でお問い合わせください。
              </p>
              <button 
                onClick={closeSuccessModal}
                className="bg-primary text-white px-6 py-2 rounded-lg cursor-pointer whitespace-nowrap hover:bg-green-700 transition-colors"
              >
                <span className="text-green-100">確認</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
